import { createContext, useContext } from 'react';
import { useContentContext } from './content-context';
import { Address } from './Membership/membership-form-template/contracts';
import { useEntitySummary } from './Membership/query-hooks/query-implementations';

type EntitySummaryContext = {
	person?: { name: string; primaryEmail?: string; primaryAddress?: Address; firstName?: string; lastName?: string };
	organisation?: { name: string; primaryEmail?: string; primaryAddress?: Address };
};

const Context = createContext<EntitySummaryContext | undefined>(undefined);
Context.displayName = 'EntitySummaryContext';

export function EntitySummaryContextProvider({
	children,
	usePersonSummary,
	useOrganisationSummary
}: {
	usePersonSummary?: boolean;
	useOrganisationSummary?: boolean;
	children: React.ReactNode;
}) {
	const { contentId, requestToken, isLoggedIn } = useContentContext();

	const { data: personData, loading: personLoading } = useEntitySummary({
		contentId,
		requestToken,
		skip: !isLoggedIn || !usePersonSummary,
		useLinkedOrganisationData: false
	});

	const { data: orgData, loading: orgLoading } = useEntitySummary({
		contentId,
		requestToken,
		skip: !isLoggedIn || !useOrganisationSummary,
		useLinkedOrganisationData: true
	});

	const contextValue: EntitySummaryContext = {
		person: personData,
		organisation: orgData
	};

	if (personLoading || orgLoading) {
		return <></>;
	}

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function useEntitySummaryContext() {
	const context = useContext(Context);

	if (!context) throw new Error('Missing entity summary context');

	return context;
}
